import { PermissionsEnum } from '../constants'
import DialogUtil from './dialogUtil.js'
import LoggerUtil from './loggerUtil.js'

class PermissionsUtil {
  /**
   * Gets the status of the passed in permission.
   * @param {String} permission - The permission to check.
   * @returns {Promise<(String|Boolean)>} Either a permission status string or true false (for notifications permissions).
   */
  static async CheckPermission (permission) {
    if (!window.hasOwnProperty('cordova')) {
      return true
    }

    const permissionClass = getPermissionClass(permission)

    return permissionClass.GetStatus()
  }

  /**
   * Asks for the passed in permission, if necessary.
   * @param {String} permission - The permission to request.
   * @returns {Promise<Boolean>} True if permission were given.
   */
  static async RequestPermission (permission) {
    if (('geolocation' in navigator) && !window.hasOwnProperty('cordova')) {
      return true
    }

    const permissionClass = getPermissionClass(permission)

    return permissionClass.Request()
  }

  static async openAppSettingsPage () {
    if (!window.hasOwnProperty('cordova')) {
      return
    }

    return new Promise((resolve, reject) => {
      window.cordova.plugins.diagnostic.switchToSettings(() => {
        LoggerUtil.info('Opened app settings page successfully')
        resolve()
      }, async (error) => {
        LoggerUtil.error('Error opening app settings page')
        LoggerUtil.debug(error)
        await DialogUtil.errorAlert('Failed to open the app settings page. Please try navigating to the page manually.')
        resolve() // TODO: Throw here instead and handle in calling methods
      })
    })
  }
}

export default PermissionsUtil

const getPermissionClass = (permission) => {
  switch (permission.toUpperCase()) {
    case PermissionsEnum.Calendar.toUpperCase():
      return new CalendarPermission()

    case PermissionsEnum.Camera.toUpperCase():
      return new CameraPermission()

    case PermissionsEnum.File.toUpperCase():
      return new FilePermission()

    case PermissionsEnum.Location.toUpperCase():
      return new LocationPermission()

    case PermissionsEnum.Notifications.toUpperCase():
      return new NotificationPermission()
  }
}

class Permission {
  async GetRequestSlot (attempts = 5) {
    const platform = window.device.platform.toLowerCase()
    if (platform === 'android' && window.cordova.plugins.diagnostic.isRequestingPermission()) {
      if (attempts > 0) {
        return new Promise((resolve, reject) => {
          window.cordova.plugins.diagnostic.registerPermissionRequestCompleteHandler(statuses => {
            try {
              window.cordova.plugins.diagnostic.registerPermissionRequestCompleteHandler(null)
              resolve(this.GetRequestSlot(attempts--)) // Make sure a new request isn't on the stack
            } catch (error) {
              LoggerUtil.error('Error on permission slot callback')
              reject(error)
            }
          })
        })
      } else {
        throw new Error('Number of permission attempts exceeded')
      }
    }
  }
}

class CalendarPermission extends Permission {
  /**
   * Gets the current status of calendar permissions.
   * @returns {Promise<String>} The current status of the permission (constants contained within window.cordova.plugins.diagnostic.permissionStatus).
   */
  async GetStatus () {
    return new Promise((resolve, reject) => window.cordova.plugins.diagnostic.getCalendarAuthorizationStatus(resolve, reject))
  }

  /**
   * Requests calendar permissions if necessary and returns the result.
   * @returns {Promise<Boolean>} True if permission is granted, false otherwise.
   */
  async Request () {
    let hasPermission = false

    await this.GetRequestSlot()

    // Check the current permission state
    let status = await this.GetStatus()
    if (status === window.cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED || status === window.cordova.plugins.diagnostic.permissionStatus.DENIED_ONCE) {
      // If we don't have access and can ask, then ask
      status = await new Promise((resolve, reject) => window.cordova.plugins.diagnostic.requestCalendarAuthorization(resolve, reject))
      hasPermission = status === window.cordova.plugins.diagnostic.permissionStatus.GRANTED
    } else if (status === window.cordova.plugins.diagnostic.permissionStatus.GRANTED) {
      // If permission was granted, continue
      hasPermission = true
    }

    return hasPermission
  }
}

class CameraPermission extends Permission {
  /**
   * Gets the current status of camera permissions.
   * @returns {Promise<String>} The current status of the permission (constants contained within window.cordova.plugins.diagnostic.permissionStatus).
   */
  async GetStatus () {
    // Check the current permission state
    return new Promise((resolve, reject) => window.cordova.plugins.diagnostic.getCameraAuthorizationStatus(resolve, reject, false))
  }

  /**
   * Requests camera permissions if necessary and returns the result.
   * @returns {Promise<Boolean>} True if permission is granted, false otherwise.
   */
  async Request () {
    let hasPermission = false

    await this.GetRequestSlot()

    // Check the current permission state
    let status = await this.GetStatus()
    if (status === window.cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED || status === window.cordova.plugins.diagnostic.permissionStatus.DENIED_ONCE) {
      // If we don't have access and can ask, then ask
      status = await new Promise((resolve, reject) => window.cordova.plugins.diagnostic.requestCameraAuthorization(resolve, reject, false))
      hasPermission = status === window.cordova.plugins.diagnostic.permissionStatus.GRANTED
    } else if (status === window.cordova.plugins.diagnostic.permissionStatus.GRANTED) {
      // If permission was granted, continue
      hasPermission = true
    }

    return hasPermission
  }
}

class FilePermission extends Permission {
  /**
   * Gets the current status of file permissions.
   * @returns {Promise<String>} The current status of the permission (constants contained within window.cordova.plugins.diagnostic.permissionStatus).
   */
  async GetStatus () {
    const platform = window.device.platform.toLowerCase()

    if (platform === 'ios') { // iOS devices already have permission by default
      return window.cordova.plugins.diagnostic.permissionStatus.GRANTED
    }

    // Check the current permission state
    const result = await new Promise((resolve, reject) => window.cordova.plugins.diagnostic.getExternalStorageAuthorizationStatus(resolve, reject))

    return result
  }

  /**
   * Requests file permissions if necessary and returns the result.
   * @returns {Promise<Boolean>} True if permission is granted, false otherwise.
   */
  async Request () {
    const platform = window.device.platform.toLowerCase()

    if (platform === 'ios') { // iOS devices already have permission by default
      return true
    }

    let hasPermission = false

    await this.GetRequestSlot()

    // Check the current permission state
    let status = await this.GetStatus()

    if (status === window.cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED || status === window.cordova.plugins.diagnostic.permissionStatus.DENIED_ONCE) {
      // If we don't have access and can ask, then ask
      status = await new Promise((resolve, reject) => window.cordova.plugins.diagnostic.requestExternalStorageAuthorization(resolve, reject))
      hasPermission = status === window.cordova.plugins.diagnostic.permissionStatus.GRANTED
    } else if (status === window.cordova.plugins.diagnostic.permissionStatus.GRANTED) {
      // If permission was granted, continue
      hasPermission = true
    }

    return hasPermission
  }
}

class LocationPermission extends Permission {
  /**
   * Gets the current status of GPS permissions.
   * @returns {Promise<String>} The current status of the permission (constants contained within window.cordova.plugins.diagnostic.permissionStatus).
   */
  async GetStatus () {
    // Check the current permission state
    let enabledResult = true

    if (window.device.platform.toLowerCase() === 'ios') {
      enabledResult = await new Promise((resolve, reject) => window.cordova.plugins.diagnostic.isLocationEnabled(resolve, reject)) // Is Location service turned on? (iOS does not allow permissions requests if not)
    }

    LoggerUtil.debug('Location services enabled: ' + enabledResult)

    if (enabledResult === false) {
      return 'location_services_disabled' // If Location services is off (on iOS), we cannot check if permissions have been requested or not
    }

    return new Promise((resolve, reject) => window.cordova.plugins.diagnostic.getLocationAuthorizationStatus(resolve, reject))
  }

  /**
   * Requests GPS permissions if necessary and returns the result.
   * @returns {Promise<Boolean>} True if permission is granted, false otherwise.
   */
  async Request () {
    let hasPermission = false

    await this.GetRequestSlot()

    // Check the current permission state
    let status = await this.GetStatus()

    if (status === 'location_services_disabled') {
      return false
    }

    const isGrantedStatus = (status) => status === window.cordova.plugins.diagnostic.permissionStatus.GRANTED || status === window.cordova.plugins.diagnostic.permissionStatus.GRANTED_WHEN_IN_USE

    if (status === window.cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED || status === window.cordova.plugins.diagnostic.permissionStatus.DENIED_ONCE) {
      // If we don't have access and can ask, then ask
      status = await new Promise((resolve, reject) => window.cordova.plugins.diagnostic.requestLocationAuthorization(resolve, reject))
      hasPermission = isGrantedStatus(status)
    } else if (isGrantedStatus(status)) {
      // If permission was granted, continue
      hasPermission = true
    }

    return hasPermission
  }
}

class NotificationPermission extends Permission {
  /**
   * Gets the current status of notification permissions.
   * @returns {Promise<Boolean>} True if remote notifications are enabled, false otherwise.
   */
  async GetStatus () {
    return new Promise((resolve, reject) => window.cordova.plugins.diagnostic.isRemoteNotificationsEnabled(resolve, reject))
  }

  /**
   * Requests notification permissions if necessary and returns the result.
   * @returns {Promise<Boolean>} True if permission is granted, false otherwise.
   */
  async Request () {
    let hasPermission = false

    await this.GetRequestSlot()

    // Check the current permission state
    const status = await this.GetStatus()
    if (status !== true) {
      await new Promise((resolve, reject) => window.cordova.plugins.diagnostic.requestRemoteNotificationsAuthorization(resolve, reject, [window.cordova.plugins.diagnostic.remoteNotificationType.ALERT, window.cordova.plugins.diagnostic.remoteNotificationType.SOUND, window.cordova.plugins.diagnostic.remoteNotificationType.BADGE]))
        .catch(e => {
          hasPermission = false
          LoggerUtil.error('Error getting notification permissions ' + e)
        })
      hasPermission = true
    } else {
      // If permission was granted, continue
      hasPermission = true
    }

    return hasPermission
  }
}
